import { CircularProgress } from '@mui/material';
import Box from '@mui/material/Box';
import { QueryStatus } from '@reduxjs/toolkit/query';
import React from 'react';
import { Navigate } from 'react-router-dom';
import { match } from 'ts-pattern';

import { useGetTransactionQuery } from '@/api/v1';
import { ApplicationModal } from '@/components/organisms/ApplicationModal';
import { PIXQRCodeModalContent } from '@/components/organisms/PIXQRCodeModalContent';
import { SpeiDepositModalContent } from '@/components/organisms/SpeiDepositModalContent';
import { emptyTransactionResponse } from '@/data/emptyTransactionResponse';
import { useTransactionId } from '@/hooks/useTransactionId';
import { useTranslator } from '@/hooks/useTranslator';
import { CryptoAddressModal } from '@/Screens/Dashboard/Flows/common/CryptoAddressModal';
import { PaymentRailType } from '@/types/currency';
import { isPixInLocalInfo, isSpeiInLocalInfo } from '@/types/transaction.ext';
import { noop } from '@/utility/noop';

interface Props {
  onClose(): void;
}

export const DepositPayment: React.FC<Props> = (props: Props): React.ReactElement => {
  const { onClose } = props;

  const transactionId = useTransactionId();
  const tr = useTranslator();

  const { data: transaction = emptyTransactionResponse, status } =
    useGetTransactionQuery(transactionId);

  const fundingMethod = React.useMemo((): string => {
    return match(transaction.localInfo)
      .when(isSpeiInLocalInfo, (): string => PaymentRailType.SPEI)
      .when(isPixInLocalInfo, (): string => PaymentRailType.PIX)
      .otherwise((): string => PaymentRailType.Ethereum);
  }, [transaction.localInfo]);

  if (status === QueryStatus.pending || status === QueryStatus.uninitialized) {
    return (
      <ApplicationModal title="" description="" onClose={noop}>
        <Box display="flex" alignItems="center" justifyContent="center">
          <CircularProgress size={96} thickness={4} />
        </Box>
      </ApplicationModal>
    );
  } else if (status === QueryStatus.rejected) {
    return <Navigate to="/home" replace={true} />;
  } else {
    switch (fundingMethod) {
      case PaymentRailType.PIX:
        return (
          <ApplicationModal
            title={tr('SEND_FUNDS_TO_CALIZA_WALLET')}
            description={tr('PIX_DEPOSIT_DESCRIPTION')}
            back="review"
            onClose={onClose}>
            <PIXQRCodeModalContent transaction={transaction} successPath="../pending" />
          </ApplicationModal>
        );
      case PaymentRailType.SPEI:
        return (
          <ApplicationModal
            title={tr('SEND_FUNDS_TO_CALIZA_WALLET')}
            description={tr('SPEI_DEPOSIT_DESCRIPTION')}
            back="review"
            onClose={onClose}>
            <SpeiDepositModalContent transaction={transaction} successPath="../pending" />
          </ApplicationModal>
        );
      case PaymentRailType.Ethereum:
      case PaymentRailType.TRON:
      case PaymentRailType.Polygon:
        return <CryptoAddressModal transaction={transaction} />;
      default:
        console.debug(`unsupported funding method: ${fundingMethod}`);
        return <></>;
    }
  }
};
